import { sendLeadEvent } from "@/lib/lead-tracking";
import { campaignParams } from "@/lib/attribution";

type Gtag = (...args: unknown[]) => void;

/** `record: false` sends to GA4 only, for taps that another event already saves to lead_events. */
export function trackEvent(name: string, params: Record<string, unknown> = {}, { record = true }: { record?: boolean } = {}) {
  if (typeof window === "undefined") return;
  if (record) sendLeadEvent(name, params);
  const gtag = (window as unknown as { gtag?: Gtag }).gtag;
  if (typeof gtag !== "function") return;
  // Params with undefined values must not wipe campaign fields.
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined));
  gtag("event", name, { page_path: window.location.pathname, ...campaignParams(), ...clean });
}

function linkLocation(el: Element): string {
  const tagged = el.closest("[data-track-location]");
  if (tagged) return tagged.getAttribute("data-track-location") || "page";
  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  return "page";
}

const CTA_RE = /\b(check my date|book now|reserve)\b/i;

/** One delegated listener on document; survives client-side route changes. */
export function installClickTracking(): () => void {
  const onClick = (e: MouseEvent) => {
    const target = e.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest("a, button");
    if (!el) return;
    if (el.hasAttribute("data-track-manual")) return;
    const href = el.getAttribute("href") || "";
    const loc = linkLocation(el);
    // Button wording, so the dashboard can tell calls to action apart.
    const cta_label = ((el.textContent || "").replace(/\s+/g, " ").trim() || el.getAttribute("aria-label") || "").slice(0, 60) || undefined;

    if (href.startsWith("sms:")) {
      if (el.hasAttribute("data-sms-cta")) trackEvent("check_date_click", { link_location: loc, cta_label });
      trackEvent("sms_click", { link_location: loc, cta_label });
    }
    else if (href.startsWith("tel:")) {
      trackEvent("phone_click", { link_location: loc, cta_label });
      trackEvent("call_click", { link_location: loc });
    }
    else if (/^https?:\/\/(wa\.me|api\.whatsapp\.com)\//i.test(href)) trackEvent("whatsapp_click", { link_location: loc, cta_label });
    else if (href.startsWith("mailto:")) trackEvent("email_click", { link_location: loc, cta_label });
    else if (CTA_RE.test(el.textContent || "")) trackEvent("check_date_click", { link_location: loc, cta_label });
  };
  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}

/** Fires big_dates_view once per page load when [data-track-section="big-dates"] enters view. */
export function installBigDatesObserver(): () => void {
  let fired = false;
  let io: IntersectionObserver | null = null;
  const attach = () => {
    if (fired || io || typeof IntersectionObserver === "undefined") return;
    const el = document.querySelector('[data-track-section="big-dates"]');
    if (!el) return;
    io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting) && !fired) {
        fired = true;
        trackEvent("big_dates_view");
        io?.disconnect();
        mo.disconnect();
      }
    }, { threshold: 0.25 });
    io.observe(el);
  };
  // Watch the DOM so the section is picked up after client-side navigation.
  const mo = new MutationObserver(() => {
    if (io && !document.contains(document.querySelector('[data-track-section="big-dates"]'))) {
      io.disconnect();
      io = null;
    }
    attach();
  });
  mo.observe(document.body, { childList: true, subtree: true });
  attach();
  return () => { mo.disconnect(); io?.disconnect(); };
}
